import { useEffect, useState } from "react";
import { Trash2, Upload } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { blobUrl } from "@/lib/scanOptions";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

const Thumb = ({ id, height }) => {
  const [src, setSrc] = useState(null);
  useEffect(() => { blobUrl(`/banners/${id}/image`).then(setSrc).catch(() => {}); }, [id]);
  return <div className="flex-1 rounded bg-slate-100 overflow-hidden" style={{ height }}>{src && <img src={src} alt="" className="w-full h-full object-cover" />}</div>;
};

export const BannerPanel = () => {
  const [s, setS] = useState(null);
  const [items, setItems] = useState([]);
  const [nb, setNb] = useState({ title: "", link_url: "", file: null });
  const load = () => api.get("/admin/banners").then((r) => setItems(r.data));
  useEffect(() => { api.get("/admin/banner-settings").then((r) => setS(r.data)); load(); }, []);
  const err = (x) => toast.error(formatApiError(x.response?.data?.detail));
  const saveSettings = async () => {
    try { setS((await api.put("/admin/banner-settings", { enabled: s.enabled, height: Number(s.height), width: s.width ? Number(s.width) : null, rotate_seconds: Number(s.rotate_seconds) })).data); toast.success("Bant ayarları kaydedildi"); } catch (x) { err(x); }
  };
  const add = async (e) => {
    e.preventDefault();
    if (!nb.file) return toast.error("GIF veya JPG görsel seçin");
    const fd = new FormData();
    fd.append("file", nb.file);
    try { await api.post("/admin/banners", fd, { params: { title: nb.title, link_url: nb.link_url || undefined }, headers: { "Content-Type": "multipart/form-data" } }); toast.success("Bant görseli eklendi"); setNb({ title: "", link_url: "", file: null }); e.target.reset(); load(); } catch (x) { err(x); }
  };
  if (!s) return null;
  return (
    <div className="space-y-4" data-testid="banner-panel">
      <Card className="p-5 space-y-3">
        <div className="font-semibold">Ofis paneli üst reklam bandı</div>
        <label className="flex items-center gap-2 text-sm"><Switch data-testid="banner-enabled" checked={s.enabled} onCheckedChange={(v) => setS({ ...s, enabled: v })} />Bandı göster</label>
        <div className="grid grid-cols-3 gap-3">
          <div><Label>Yükseklik (px)</Label><Input data-testid="banner-height" type="number" min="16" max="300" value={s.height} onChange={(e) => setS({ ...s, height: e.target.value })} /></div>
          <div><Label>Genişlik (px, boş = tam genişlik)</Label><Input data-testid="banner-width" type="number" min="100" placeholder="Tam genişlik" value={s.width ?? ""} onChange={(e) => setS({ ...s, width: e.target.value })} /></div>
          <div><Label>Geçiş süresi (sn)</Label><Input data-testid="banner-rotate" type="number" min="2" max="120" value={s.rotate_seconds} onChange={(e) => setS({ ...s, rotate_seconds: e.target.value })} /></div>
        </div>
        <div className="flex justify-end"><Button data-testid="banner-settings-save" onClick={saveSettings}>Kaydet</Button></div>
      </Card>
      <Card className="p-5 space-y-3">
        <div className="font-semibold">Bant görselleri (sırayla döner)</div>
        <form onSubmit={add} className="grid md:grid-cols-[1fr_1fr_1fr_auto] gap-2 items-end">
          <div><Label className="text-xs">Başlık *</Label><Input data-testid="banner-title" required minLength={2} value={nb.title} onChange={(e) => setNb({ ...nb, title: e.target.value })} /></div>
          <div><Label className="text-xs">Bağlantı</Label><Input data-testid="banner-link" placeholder="https://" value={nb.link_url} onChange={(e) => setNb({ ...nb, link_url: e.target.value })} /></div>
          <div><Label className="text-xs">Görsel (GIF/JPG/PNG)</Label><Input data-testid="banner-file" type="file" accept="image/gif,image/jpeg,image/png,image/webp" onChange={(e) => setNb({ ...nb, file: e.target.files[0] })} /></div>
          <Button data-testid="banner-add-btn" type="submit" className="bg-slate-900"><Upload className="w-4 h-4 mr-1" />Ekle</Button>
        </form>
        {items.length === 0 && <div className="text-sm text-slate-500">Henüz bant görseli yok</div>}
        {items.map((b) => (
          <div key={b.id} data-testid={`banner-row-${b.id}`} className="flex items-center gap-3 rounded-lg border p-2">
            <Thumb id={b.id} height={Math.min(Number(s.height) || 25, 60)} />
            <div className="w-40 text-sm truncate">{b.title}</div>
            <Switch data-testid={`banner-active-${b.id}`} checked={b.active} onCheckedChange={async (v) => { await api.put(`/admin/banners/${b.id}`, { title: b.title, link_url: b.link_url, active: v }); load(); }} />
            <Button size="icon" variant="ghost" data-testid={`banner-delete-${b.id}`} onClick={async () => { await api.delete(`/admin/banners/${b.id}`); load(); }}><Trash2 className="w-4 h-4 text-red-600" /></Button>
          </div>
        ))}
      </Card>
    </div>
  );
};
